/**
 * MaintenanceModal — Platform-Admin-only control for the app-wide
 * kill-switch. Sits behind the Admin → Maintenance card.
 *
 * 2026-09-27 (Dave) — v1 spec:
 *   • Toggle switch: OFF ↔ ON
 *   • Start-at + Back-at datetime-local pickers (user's local
 *     timezone; converted to UTC ISO before POST)
 *   • Optional 200-char note
 *   • Confirm step: user must type DEACTIVATE to arm the toggle
 *     when flipping ON (irreversible-feeling gate). Flipping OFF is
 *     a single click — recovering from maintenance shouldn't have
 *     extra friction.
 *   • On success, dispatches `ureserve:platform-state-changed` so
 *     App.js re-polls the state immediately.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { X, Wrench } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// datetime-local requires a `YYYY-MM-DDTHH:MM` string in local time.
// The browser then submits it as the same wall-clock string; we treat
// it as local, convert to UTC ISO for the API.
function _isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
function _localInputToIso(local) {
  if (!local) return null;
  // Interpret local time in the browser's timezone → convert to UTC ISO.
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export default function MaintenanceModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [state, setState] = useState({
    is_deactivated: false, start_at: null, back_at: null, note: "",
  });
  const [form, setForm] = useState({ start_at: "", back_at: "", note: "" });
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState(null);
  const firstFieldRef = useRef(null);

  // Hydrate on open + poll every 5 s while open so the admin's panel
  // stays in sync with server-side transitions (auto-lift at back_at,
  // another admin flipping it, etc). Without this the modal kept
  // showing "ON" long after the window expired — Dave's 2026-09-27
  // report against the CDT test window.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const fetchOnce = () => {
      axios.get(`${API}/platform/state`)
        .then((r) => {
          if (cancelled) return;
          setState((prev) => {
            // Only overwrite the form fields on the initial load; once
            // the admin has started typing we don't want the 5-s
            // poller to yank their input out from under them.
            if (!prev || prev.updated_at !== r.data.updated_at) {
              setForm({
                start_at: _isoToLocalInput(r.data.start_at),
                back_at: _isoToLocalInput(r.data.back_at),
                note: r.data.note || "",
              });
              setConfirmText("");
            }
            return r.data;
          });
        })
        .catch((e) => {
          if (!cancelled) setError(e?.response?.data?.detail || e.message);
        })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [open]);

  // Autofocus the start-at field so keyboard users can dive right in.
  useEffect(() => {
    if (open && !loading && firstFieldRef.current) {
      firstFieldRef.current.focus();
    }
  }, [open, loading]);

  const flipOn = async () => {
    if (confirmText !== "DEACTIVATE") {
      setError('Type DEACTIVATE (all caps) to confirm.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        is_deactivated: true,
        start_at: _localInputToIso(form.start_at),
        back_at: _localInputToIso(form.back_at),
        note: (form.note || "").trim(),
      };
      const r = await axios.post(`${API}/platform/state`, payload);
      setState(r.data);
      setConfirmText("");
      window.dispatchEvent(new Event("ureserve:platform-state-changed"));
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const flipOff = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/platform/state`, { is_deactivated: false });
      setState(r.data);
      setForm({ start_at: "", back_at: "", note: "" });
      window.dispatchEvent(new Event("ureserve:platform-state-changed"));
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const canArm = useMemo(() => {
    return (
      Boolean(form.start_at) &&
      Boolean(form.back_at) &&
      _localInputToIso(form.back_at) > _localInputToIso(form.start_at) &&
      confirmText === "DEACTIVATE"
    );
  }, [form, confirmText]);

  const armBlocker = !form.start_at
    ? "Pick a Start at date and time."
    : !form.back_at
    ? "Pick a Back at date and time."
    : !(_localInputToIso(form.back_at) > _localInputToIso(form.start_at))
    ? "Back at must be later than Start at."
    : confirmText !== "DEACTIVATE"
    ? "Type DEACTIVATE (all caps) to enable the button."
    : null;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="maintenance-modal-backdrop"
    >
      <div
        className="bg-white rounded-[6px] w-[540px] max-w-[92vw] shadow-xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="maintenance-modal"
        role="dialog"
        aria-labelledby="maintenance-modal-title"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-3 rounded-t-[6px]"
          style={{ background: "#0B2545", color: "#fff" }}
        >
          <div className="flex items-center gap-2">
            <Wrench size={17} strokeWidth={2.1} />
            <span
              id="maintenance-modal-title"
              className="font-display text-[15px] font-bold tracking-wide"
            >
              Maintenance Mode
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 hover:bg-white/15"
            aria-label="Close"
            data-testid="maintenance-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4 text-[13px]">
          {loading ? (
            <div className="text-slate italic py-6 text-center">Loading…</div>
          ) : state.is_deactivated ? (
            <>
              <div className="rounded-[3px] p-3 border border-coral/60 bg-coral/5 text-navy space-y-1.5">
                <div className="font-bold">
                  Maintenance is currently{" "}
                  <span className="uppercase tracking-[0.09em]">
                    {state.phase === "scheduled" ? "scheduled" : "on"}
                  </span>
                  .
                </div>
                {/* 2026-09-27 v3 — always surface the two timestamps
                    + optional note so the platform admin sees the
                    exact window without re-loading /platform/state. */}
                <div className="grid grid-cols-[80px_1fr] gap-x-3 gap-y-0.5 text-[12px] pt-1">
                  <div className="uppercase text-[10px] tracking-[0.09em] text-slate">Starts</div>
                  <div className="font-mono tabular-nums" data-testid="maintenance-modal-start-echo">
                    {state.start_at
                      ? new Date(state.start_at).toLocaleString(undefined, {
                          weekday: "short", month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit", timeZoneName: "short",
                        })
                      : "—"}
                  </div>
                  <div className="uppercase text-[10px] tracking-[0.09em] text-slate">Back at</div>
                  <div className="font-mono tabular-nums font-bold" data-testid="maintenance-modal-back-echo">
                    {state.back_at
                      ? new Date(state.back_at).toLocaleString(undefined, {
                          weekday: "short", month: "short", day: "numeric",
                          hour: "numeric", minute: "2-digit", timeZoneName: "short",
                        })
                      : "—"}
                  </div>
                  {state.note ? (
                    <>
                      <div className="uppercase text-[10px] tracking-[0.09em] text-slate">Note</div>
                      <div className="italic">{state.note}</div>
                    </>
                  ) : null}
                </div>
                <div className="text-[12px] text-slate pt-1">
                  {state.phase === "scheduled"
                    ? "Users see a warning banner (≤15 min lead). The full lockout kicks in at the start time."
                    : "All non-Platform-Admin traffic sees the maintenance page. Auto-lifts at the Back-at time."}
                </div>
              </div>

              <button
                type="button"
                onClick={flipOff}
                disabled={saving}
                className="w-full py-2 rounded-[3px] text-white font-semibold hover:opacity-90 disabled:opacity-50"
                style={{ background: "#0B2545" }}
                data-testid="maintenance-flip-off"
              >
                {saving ? "Turning off…" : "Turn Off Maintenance Mode"}
              </button>
            </>
          ) : (
            <>
              <div className="text-slate text-[12px]">
                While maintenance is ON, every non-admin request is refused
                with a full-screen page carrying the times below. Platform
                Admins keep full access so they can turn it back off.
              </div>

              <div className="space-y-3">
                <label className="block">
                  <span className="uppercase text-[10px] tracking-[0.09em] font-semibold text-navy">
                    Start at
                  </span>
                  <input
                    ref={firstFieldRef}
                    type="datetime-local"
                    value={form.start_at}
                    onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))}
                    className="mt-1 w-full border border-slate/40 rounded-[3px] px-2 py-1.5 font-mono text-[13px]"
                    data-testid="maintenance-start-input"
                  />
                </label>

                <label className="block">
                  <span className="uppercase text-[10px] tracking-[0.09em] font-semibold text-navy">
                    Back at
                  </span>
                  <input
                    type="datetime-local"
                    value={form.back_at}
                    onChange={(e) => setForm((f) => ({ ...f, back_at: e.target.value }))}
                    className="mt-1 w-full border border-slate/40 rounded-[3px] px-2 py-1.5 font-mono text-[13px]"
                    data-testid="maintenance-back-input"
                  />
                </label>

                <label className="block">
                  <span className="uppercase text-[10px] tracking-[0.09em] font-semibold text-navy">
                    Note <span className="text-slate normal-case text-[10px]">(optional, ≤ 200 chars)</span>
                  </span>
                  <input
                    type="text"
                    maxLength={200}
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                    placeholder="e.g., Rolling out engine tweaks"
                    className="mt-1 w-full border border-slate/40 rounded-[3px] px-2 py-1.5"
                    data-testid="maintenance-note-input"
                  />
                </label>

                <label className="block">
                  <span className="uppercase text-[10px] tracking-[0.09em] font-semibold text-coral">
                    Type DEACTIVATE to confirm
                  </span>
                  <input
                    type="text"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="DEACTIVATE"
                    className="mt-1 w-full border border-coral/60 rounded-[3px] px-2 py-1.5 font-mono"
                    autoComplete="off"
                    data-testid="maintenance-confirm-input"
                  />
                </label>
              </div>

              <button
                type="button"
                onClick={flipOn}
                disabled={!canArm || saving}
                className="w-full py-2 rounded-[3px] text-white font-semibold hover:opacity-90 disabled:opacity-40"
                style={{ background: "#EA6A47" }}
                data-testid="maintenance-flip-on"
              >
                {saving ? "Turning on…" : "Turn On Maintenance Mode"}
              </button>
              {armBlocker && !saving ? (
                <div className="text-[11.5px] text-slate text-center -mt-2" data-testid="maintenance-arm-blocker">
                  {armBlocker}
                </div>
              ) : null}
            </>
          )}

          {error ? (
            <div
              className="text-[12px] text-coral border border-coral/60 rounded-[3px] px-2 py-1.5"
              data-testid="maintenance-error"
            >
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
